<template>
  <div class="home-page" :style="pageStyle">
    <!-- 自定义loading遮罩 -->
    <div v-if="loading" class="loading-mask">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <!-- 加载失败提示 -->
    <div v-if="loadError" class="error-tip">
      <p>配置加载失败，请刷新重试</p>
      <button @click="loadConfig">重试</button>
    </div>

    <!-- 动态渲染组件 -->
    <template v-else-if="pageConfig">
      <DynamicComponent
        v-for="component in visibleComponents"
        :key="component.id"
        :config="component"
        :priceData="selectedPriceData"
        @action="handleComponentAction"
        @submit="handleFormSubmit"
      />
    </template>

    <!-- 签约状态提示遮罩 -->
    <div v-if="showContractModal" class="contract-modal">
      <div class="modal-content">
<!--        <div class="modal-icon" :class="contractStatusClass">-->
<!--          <span v-if="contractStatus === 'pending'">⏳</span>-->
<!--          <span v-else-if="contractStatus === 'success'">✓</span>-->
<!--          <span v-else>✗</span>-->
<!--        </div>-->
        <div class="modal-title">{{ contractStatusText }}</div>
        <div class="modal-desc">{{ contractMessage }}</div>
        <button v-if="contractStatus === 'failed'" class="modal-btn" @click="closeContractModal">
          关闭
        </button>
      </div>
    </div>

    <!-- 底部占位 -->
    <div class="bottom-placeholder"></div>
  </div>
</template>

<script>
import DynamicComponent from '../components/DynamicComponent.vue'
import { defaultConfig } from '../config/pageConfig'
import { trackMixin } from '@/mixins/trackMixin'
import { get, post } from '@/util/request'
import {create} from "@/api/system/h5";

export default {
  mixins: [trackMixin],
  name: 'HomePage',
  components: {
    DynamicComponent
  },
  data() {
    return {
      pageConfig: null,
      loading: false,
      loadError: false,
      // 价格选择器选中的数据
      selectedPriceData: null,
      paymentProductCode: null,
      // 松球支付相关
      showContractModal: false,
      contractStatus: 'pending', // pending, success, failed
      contractMessage: '',
      currentOrderNo: null,
      // 轮询相关
      pollTimer: null,
      pollRetryCount: 0,
      maxPollRetries: 30,
      pollInterval: 3000
    }
  },
  computed: {
    // 从 URL 参数获取页面编码
    pageCode() {
      // 支持两种方式：
      // 1. URL 查询参数：?pageCode=health_welfare_h5
      // 2. URL 路径参数：/page/health_welfare_h5
      return this.$route.query.pageCode ||
             this.$route.params.pageCode ||
             'health_welfare_h5' // 默认页面编码
    },
    // 页面样式
    pageStyle() {
      if (!this.pageConfig?.page) return {}
      const { backgroundImage, backgroundColor } = this.pageConfig.page
      return {
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundColor: backgroundColor || '#fff',
        backgroundSize: '100% auto'
      }
    },
    // 可见的组件列表
    visibleComponents() {
      return this.pageConfig?.components?.filter(c => c.visible !== false) || []
    },
    // 判断是否是松球支付
    isSongQiuPayment() {
      return this.paymentProductCode?.toUpperCase()?.includes('SONGQIU') || false
    },
    // 签约状态样式
    contractStatusClass() {
      return {
        'pending': this.contractStatus === 'pending',
        'success': this.contractStatus === 'success',
        'failed': this.contractStatus === 'failed'
      }
    },
    // 签约状态文本
    contractStatusText() {
      const texts = {
        pending: '签约中',
        success: '签约成功',
        failed: '签约失败'
      }
      return texts[this.contractStatus] || ''
    }
  },
  watch: {
    // 监听页面编码变化，重新加载配置
    pageCode: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.waitForMonitoAndLoad()
        }
      }
    }
  },
  created() {
    this.waitForMonitoAndLoad()
  },
  beforeDestroy() {
    // 组件销毁时清理定时器
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
    }
  },
  methods: {
    // 等待 monitoAction 初始化完成后加载配置
    async waitForMonitoAndLoad() {
      // 等待 monitoAction 初始化完成
      await this.waitForMonitoAction()
      // 然后加载配置
      await this.loadConfig()
    },

    // 等待 monitoAction 初始化
    waitForMonitoAction() {
      return new Promise((resolve) => {
        // 如果已经初始化完成，直接返回
        if (this.$monitoAction) {
          resolve()
          return
        }

        // 最多等待 5 秒
        let attempts = 0
        const maxAttempts = 50 // 50 * 100ms = 5s
        const checkInterval = setInterval(() => {
          attempts++
          if (this.$monitoAction) {
            clearInterval(checkInterval)
            resolve()
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval)
            console.warn('monitoAction 初始化超时，继续加载页面配置')
            resolve()
          }
        }, 100)
      })
    },

    // 从 API 加载配置
    async loadConfig() {
      this.loading = true
      this.loadError = false

      try {
        // 调用 API 获取页面配置
        const response = await this.fetchPageConfig(this.pageCode)

        if (response.code === 200 && response.data) {
          this.pageConfig = response.data
          this.paymentProductCode = response.data.payment.productCode

          // 设置页面标题
          if (this.pageConfig?.page?.title) {
            document.title = this.pageConfig.page.title
          }
        } else {
          throw new Error(response.message || '获取配置失败')
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        this.loadError = true

        // 使用默认配置作为降级方案
        this.pageConfig = defaultConfig

        // 显示错误提示（可选）
        this.$toast && this.$toast('配置加载失败，使用默认配置')
      } finally {
        this.loading = false
      }
    },

    // 获取页面配置 API
    async fetchPageConfig(pageCode) {
      // 获取 URL 参数中的版本号和 A/B 测试分组
      const params = {
        version: this.$route.query.version,
        abTestGroup: this.$route.query.abTestGroup
      }

      // 使用 request.js 发送请求
      return await get(`/api/public/${pageCode}`, params)
    },

    // 处理组件事件
    handleComponentAction({ componentId, componentType, ...action }) {
      console.log('组件事件:', componentId, componentType, action)

      // 处理价格选择器的事件
      if (componentType === 'PriceSelector' && action.type === 'priceSelect') {
        this.selectedPriceData = action.data
      }

      // 可以在这里处理全局事件，如统计、跳转等
      // 例如：上报组件交互数据
      this.reportComponentAction(componentId, componentType, action)
    },

    // 处理表单提交
    async handleFormSubmit(data) {
      data.paymentProductCode = this.paymentProductCode
      console.log('表单提交数据:', data)

      const params = {
        phone: data.phone,
        agreed: data.agreed,
        packageCode: data.priceData.package,
        paymentProductCode: data.paymentProductCode,
        amount: data.priceData.price,
        pageCode: this.pageCode,
        ln: this.$route.query.ln || ''
      }

      if (this.isSongQiuPayment) {
        await this.handleSongQiuPayment(params)
      } else {
        // 其他支付方式走原有流程
        create(params).then(res => {
          console.log(res)
          this.$toast('提交成功')
        })
      }
    },

    // 松球支付流程
    async handleSongQiuPayment(params) {
      try {
        // Step 1: 创建订单
        this.loading = true
        const createRes = await post('/api/payment/create', params)

        console.log('创建订单响应:', createRes)

        // 兼容数字0和字符串'0'
        if (createRes.code != 200 && createRes.code !== '200') {
          throw new Error(createRes.msg || '创建订单失败')
        }

        this.currentOrderNo = createRes.data.orderNo
        console.log('订单创建成功:', this.currentOrderNo)

        // Step 2: 组装小程序跳转链接
        const jumpUrl = this.buildMiniProgramUrl({
          orderNo: this.currentOrderNo,
          phone: params.phone,
          nickName: '',
          description: '会员自动续费',
          sex: 0,
          amount: params.amount
        })

        console.log('小程序跳转链接:', jumpUrl)

        // Step 3: 显示签约中提示并跳转小程序
        this.showContractModal = true
        this.contractStatus = 'pending'
        this.contractMessage = '正在跳转到微信小程序签约，请完成签约操作...'
        this.loading = false

        // 跳转小程序（由客户端APP处理URL Scheme）
        this.jumpToMiniProgram(jumpUrl)

        // Step 4: 开始轮询查询签约结果
        this.startPollContractStatus()

      } catch (error) {
        console.error('松球支付失败:', error.message)
        this.loading = false
        this.$toast && this.$toast(error.message)
      }
    },

    // 构建小程序跳转链接
    // 文档要求：query 后所有参数必须做 URL编码，中文、特殊字符需转义
    // 注意：& 和 = 作为参数分隔符不需要编码，只编码参数值
    buildMiniProgramUrl(params) {
      const appid = 'wxfa5e28c2ecfeeedb'
      const path = 'pages/sign/index'

      // 手动构建query参数，只对参数值进行编码，&和=不编码
      const queryParams = []
      if (params.orderNo) queryParams.push(`orderNo=${encodeURIComponent(params.orderNo)}`)
      if (params.phone) queryParams.push(`phone=${encodeURIComponent(params.phone)}`)
      if (params.nickName) queryParams.push(`nickName=${encodeURIComponent(params.nickName)}`)
      if (params.description) queryParams.push(`description=${encodeURIComponent(params.description)}`)
      if (params.sex) queryParams.push(`sex=${encodeURIComponent(params.sex)}`)
      if (params.amount) queryParams.push(`amount=${encodeURIComponent(params.amount)}`)
      // 用&连接参数（&不被编码）
      const queryString = queryParams.join('&')
      // query参数后直接拼接，不需要再编码（&和=保持原样）
      const url = `weixin://dl/business/?appid=${appid}&path=${path}&query=${queryString}`
      console.log('生成的小程序跳转链接:', url)
      return url
    },

    // 跳转小程序（由客户端处理）
    jumpToMiniProgram(url) {
      console.log('跳转小程序:', url)

      // 方案1：动态创建a标签点击跳转（兼容性更好）
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // 方案2：iOS Safari兼容方案 - 使用iframe
      setTimeout(() => {
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = url
        document.body.appendChild(iframe)

        setTimeout(() => {
          document.body.removeChild(iframe)
        }, 500)
      }, 100)

      // 方案3：如果是APP内嵌H5，可以通过JSBridge通知APP跳转
      // if (window.JSBridge && window.JSBridge.callHandler) {
      //   window.JSBridge.callHandler('jumpToMiniProgram', { url: url })
      // }

      // 方案4：微信浏览器内使用微信JS-SDK
      // if (window.wx && window.wx.miniProgram) {
      //   window.wx.miniProgram.navigateTo({ url: url })
      // }
    },

    // 开始轮询查询签约结果
    startPollContractStatus() {
      this.pollRetryCount = 0
      this.pollContractStatus()
    },

    // 轮询查询签约结果
    async pollContractStatus() {
      if (this.pollRetryCount >= this.maxPollRetries) {
        this.contractStatus = 'failed'
        this.contractMessage = '查询超时，请稍后重试'
        this.pollTimer = null
        return
      }

      this.pollRetryCount++

      try {
        const res = await post('/api/payment/query', {
          orderNo: this.currentOrderNo,
          paymentProductCode: this.paymentProductCode
        })

        // 兼容数字0和字符串'0'
        if (res.code != 0 && res.code !== '0') {
          throw new Error(res.msg)
        }

        const data = res.data
        const status = data.status

        // 签约成功
        if (status === 1) {
          this.contractStatus = 'success'
          this.contractMessage = `签约成功！协议号：${data.extra?.contractId || '未知'}`
          this.pollTimer = null
          // 3秒后关闭弹窗并跳转到成功页面
          setTimeout(() => {
            this.closeContractModal()
            this.redirectToSuccess()
          }, 3000)
          return
        }

        // 签约失败或已解约
        if (status === 2 || status === 3) {
          this.contractStatus = 'failed'
          this.contractMessage = data.message || '签约失败，请重新签约'
          this.pollTimer = null
          return
        }

        // 签约中，继续轮询
        this.pollTimer = setTimeout(() => {
          this.pollContractStatus()
        }, this.pollInterval)

      } catch (error) {
        console.error('查询签约结果失败:', error.message)
        // 失败后继续重试
        this.pollTimer = setTimeout(() => {
          this.pollContractStatus()
        }, this.pollInterval)
      }
    },

    // 关闭签约弹窗
    closeContractModal() {
      this.showContractModal = false
      this.contractStatus = 'pending'
      this.contractMessage = ''
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },

    // 跳转到成功页面
    redirectToSuccess() {
      const successUrl = `/success?orderNo=${this.currentOrderNo}`
      if (this.$router) {
        this.$router.push(successUrl)
      } else {
        window.location.href = successUrl
      }
    },

    // 上报组件交互数据（可选）
    async reportComponentAction(componentId, componentType, action) {
      try {
        // 发送统计请求
        // await this.$http.post('/api/v1/page-config/action-log', {
        //   pageCode: this.pageCode,
        //   componentId,
        //   componentType,
        //   action,
        //   timestamp: new Date().toISOString()
        // })
      } catch (error) {
        console.error('上报组件事件失败:', error)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.home-page {
  position: relative;
  min-height: 100vh;
  padding-bottom: 0.5rem;
}

.bottom-placeholder {
  height: 0.5rem;
}

/* 自定义loading样式 */
.loading-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999;

  .loading-spinner {
    width: 0.8rem;
    height: 0.8rem;
    border: 0.06rem solid #E7401E;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  p {
    margin-top: 0.3rem;
    color: #666;
    font-size: 0.28rem;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 0.5rem;

  p {
    color: #666;
    font-size: 0.28rem;
    margin-bottom: 0.3rem;
  }

  button {
    padding: 0.15rem 0.4rem;
    background: #E7401E;
    color: #fff;
    border: none;
    border-radius: 0.3rem;
    font-size: 0.28rem;
    cursor: pointer;

    &:active {
      opacity: 0.8;
    }
  }
}

/* 签约弹窗样式 */
.contract-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 0.5rem;

  .modal-content {
    background: #fff;
    border-radius: 0.2rem;
    padding: 0.6rem;
    width: 80%;
    max-width: 320px;
    text-align: center;
    animation: fadeIn 0.3s ease;
  }

  .modal-icon {
    width: 1.2rem;
    height: 1.2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.3rem;
    font-size: 0.5rem;

    &.pending {
      background: #FFB800;
      color: #fff;
      animation: pulse 1.5s infinite;
    }

    &.success {
      background: #52C41A;
      color: #fff;
    }

    &.failed {
      background: #FF4D4F;
      color: #fff;
    }
  }

  .modal-title {
    font-size: 0.36rem;
    font-weight: bold;
    color: #333;
    margin-bottom: 0.2rem;
  }

  .modal-desc {
    font-size: 0.28rem;
    color: #666;
    line-height: 1.5;
    margin-bottom: 0.4rem;
  }

  .modal-btn {
    padding: 0.2rem 0.8rem;
    background: #E7401E;
    color: #fff;
    border: none;
    border-radius: 0.3rem;
    font-size: 0.28rem;
    cursor: pointer;

    &:active {
      opacity: 0.8;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
